<template>
    <div class="profile-page">
        <PageHeader icon="fas fa-shield-alt">
            <template #title>Ubah Password</template>
            <template #subtitle>Perbarui password secara berkala untuk menjaga keamanan akun.</template>
        </PageHeader>

        <Card card-class="profile-content-card profile-form-card" body-class="profile-content-card__body">
            <template #body>
                <b-form class="profile-form" @submit.prevent="formOnSubmit">
                    <div class="profile-form-layout profile-form-layout--password">
                        <aside class="profile-security-panel">
                            <div class="profile-avatar profile-avatar--medium">
                                <img :src="profilePhoto" :alt="`Foto profil ${displayValue(data.name)}`" @error="useDefaultAvatar" />
                                <span class="profile-avatar__status" title="Akun aktif"></span>
                            </div>
                            <h3>{{ displayValue(data.name) }}</h3>
                            <p>{{ displayValue(data.email) }}</p>

                            <div class="profile-security-note">
                                <span><i class="fas fa-lock"></i></span>
                                <div>
                                    <strong>Password yang aman</strong>
                                    <small>Gunakan minimal 10 karakter dan hindari password yang mudah ditebak.</small>
                                </div>
                            </div>
                        </aside>

                        <div class="profile-form-main">
                            <div class="profile-section-heading">
                                <span class="profile-section-heading__icon"><i class="fas fa-key"></i></span>
                                <div>
                                    <h3>Keamanan Akun</h3>
                                    <p>Masukkan password lama sebelum membuat password baru.</p>
                                </div>
                            </div>

                            <div class="profile-password-fields">
                                <b-form-group class="profile-field" label="Password Lama" label-for="oldPassword">
                                    <div class="profile-field__control profile-field__control--password">
                                        <i class="fas fa-lock"></i>
                                        <b-form-input id="oldPassword" v-model="form.old_password" :type="visibility.old" required placeholder="Masukkan password lama"></b-form-input>
                                        <button type="button" :aria-label="visibility.old === 'password' ? 'Tampilkan password lama' : 'Sembunyikan password lama'" @click="toggleVisibility('old')">
                                            <i :class="visibility.old === 'password' ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                                        </button>
                                    </div>
                                    <small v-if="error.old_password" class="text-danger">{{ error.old_password }}</small>
                                </b-form-group>

                                <b-form-group class="profile-field" label="Password Baru" label-for="newPassword">
                                    <div class="profile-field__control profile-field__control--password" :class="{ 'is-password-valid': isPasswordLengthValid }">
                                        <i class="fas fa-key"></i>
                                        <b-form-input id="newPassword" v-model="form.new_password" :type="visibility.newPassword" required placeholder="Masukkan password baru"></b-form-input>
                                        <button type="button" :aria-label="visibility.newPassword === 'password' ? 'Tampilkan password baru' : 'Sembunyikan password baru'" @click="toggleVisibility('newPassword')">
                                            <i :class="visibility.newPassword === 'password' ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                                        </button>
                                    </div>
                                    <div class="password-requirement-box mt-2" v-if="form.new_password">
                                        <span :class="isPasswordLengthValid ? 'text-success' : 'text-muted'" class="font-weight-bold d-inline-flex align-items-center">
                                            <i :class="isPasswordLengthValid ? 'fas fa-check-circle text-success' : 'far fa-circle text-muted'" class="mr-1"></i>
                                            Panjang password: {{ form.new_password.length }} karakter 
                                            <span v-if="isPasswordLengthValid">&nbsp;(Pas - Memenuhi syarat minimal 10 karakter)</span>
                                            <span v-else>&nbsp;(Minimal 10 karakter)</span>
                                        </span>
                                        <span class="badge badge-success ml-2 py-1 px-2" v-if="isPasswordLengthValid">✓ Format Pas</span>
                                    </div>
                                    <small v-if="error.new_password" class="text-danger">{{ error.new_password }}</small>
                                </b-form-group>

                                <b-form-group class="profile-field" label="Konfirmasi Password Baru" label-for="passwordConfirmation">
                                    <div class="profile-field__control profile-field__control--password" :class="{ 'is-password-valid': isPasswordMatch }">
                                        <i class="fas fa-check-circle"></i>
                                        <b-form-input id="passwordConfirmation" v-model="form.new_password_confirmation" :type="visibility.confirmation" required placeholder="Ketik ulang password baru"></b-form-input>
                                        <button type="button" :aria-label="visibility.confirmation === 'password' ? 'Tampilkan konfirmasi password' : 'Sembunyikan konfirmasi password'" @click="toggleVisibility('confirmation')">
                                            <i :class="visibility.confirmation === 'password' ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                                        </button>
                                    </div>
                                    <div class="password-requirement-box mt-2" v-if="form.new_password_confirmation">
                                        <span :class="isPasswordMatch ? 'text-success' : 'text-danger'" class="font-weight-bold d-inline-flex align-items-center">
                                            <i :class="isPasswordMatch ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-danger'" class="mr-1"></i>
                                            {{ isPasswordMatch ? 'Konfirmasi password cocok' : 'Konfirmasi password belum sama' }}
                                        </span>
                                    </div>
                                    <small v-if="error.new_password_confirmation" class="text-danger">{{ error.new_password_confirmation }}</small>
                                </b-form-group>
                            </div>
                        </div>
                    </div>

                    <div class="profile-form-actions">
                        <b-button class="profile-page__secondary-action" @click="$router.push('/profile/show')">Batal</b-button>
                        <b-button type="submit" class="profile-page__primary-action">
                            <i class="fas fa-save mr-2"></i>Simpan Password
                        </b-button>
                    </div>
                </b-form>
            </template>
        </Card>
    </div>
</template>

<script>
import Card from "@/view/content/Card.vue";
import module from "@/core/modules/CrudModule.js";
import validation from "@/core/modules/ValidationModule.js";
import { getUser } from "@/core/services/jwt.service.js";

export default {
    components: { Card },
    data() {
        return {
            data: getUser() || {},
            defaultAvatar: "/media/users/user-default.svg",
            form: {
                old_password: "",
                new_password: "",
                new_password_confirmation: "",
                _method: "put",
            },
            error: {
                old_password: "",
                new_password: "",
                new_password_confirmation: "",
            },
            visibility: {
                old: "password",
                newPassword: "password",
                confirmation: "password",
            },
        };
    },
    computed: {
        profilePhoto() {
            return this.data.photo || this.defaultAvatar;
        },
        isPasswordLengthValid() {
            return (this.form.new_password || "").length >= 10;
        },
        isPasswordMatch() {
            return Boolean(
                this.form.new_password &&
                this.form.new_password_confirmation &&
                this.form.new_password === this.form.new_password_confirmation
            );
        },
    },
    methods: {
        displayValue(value) {
            return value || "Belum tersedia";
        },
        useDefaultAvatar(event) {
            if (!event.target.src.endsWith(this.defaultAvatar)) event.target.src = this.defaultAvatar;
        },
        toggleVisibility(field) {
            this.visibility[field] = this.visibility[field] === "password" ? "text" : "password";
        },
        async formOnSubmit() {
            const response = await module.submit(this.form, `profile/change-password/${this.data.user_id}`);
            if (response.state === "error") {
                this.error = validation.setValidationError(this.error, response.error.errors);
                return;
            }
            try {
                const cur = getUser() || {};
                cur.must_change_password = false;
                window.localStorage.setItem("user", JSON.stringify(cur));
            } catch (e) {}
            Swal.fire(response.success.title, response.success.message, "success");
            this.$router.push({ name: "dashboard" });
        },
    },
};
</script>

<style lang="scss" src="@/assets/sass/pages/_profile-pages.scss"></style>
